import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDrag } from 'react-dnd';
import { format, differenceInCalendarDays, parseISO, addDays as addDaysFn } from 'date-fns';
import { updatePlan, actions } from '../../store/slice';
import { selectPrograms, selectCompletionStatus, selectCompletionStatusLoading } from '../../store/selectors';
import { DRAG_TYPES } from './constants';
import { getProgramColor, getSegmentedColors } from './programVisuals';
import { toDateOnly } from './dateUtils';

function ProgramBar({ program, cellWidth, startIso, onDoubleClick, onProgramResize, viewMode, headerData, horizontalIdx = 0, overlapCount = 1, totalSegmentWidth = null, isSearchMatch = false }) {
  const dispatch = useDispatch();
  const [isHoveringBar, setIsHoveringBar] = useState(false);
  const [isHoveringResizeHandle, setIsHoveringResizeHandle] = useState(false);
  const allProgramsFromStore = useSelector(selectPrograms);
  const completionStatus = useSelector(selectCompletionStatus);
  const completionStatusLoading = useSelector(selectCompletionStatusLoading);
  
  // Get completion status for this program
  const programId = program.id || program.originalId;
  // Convert programId to string for consistent lookup (ProcessId in completionStatus is stored as string)
  const programIdStr = programId ? String(programId) : null;
  const programCompletion = programIdStr && completionStatus && typeof completionStatus === 'object' 
    ? completionStatus[programIdStr] 
    : null;

  const [{ isDragging }, drag] = useDrag(() => ({
    type: DRAG_TYPES.PROGRAM,
    item: () => {
      const channelIdentifier = program.channelUuid || program.channelId || program.assetNumber || program.channel || program.machineId;
      const programId = program.originalId || program.id;
      return { id: programId, channelUuid: channelIdentifier, since: program.since, till: program.till };
    },
    canDrag: () => !isHoveringResizeHandle,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  }));

  const [{ isDragging: isResizingLeft }, dragLeft] = useDrag(() => ({
    type: DRAG_TYPES.RESIZE_LEFT,
    item: () => {
      const channelIdentifier = program.channelUuid || program.channelId || program.assetNumber || program.channel || program.machineId;
      const programId = program.originalId || program.id;
      if (!programId || !channelIdentifier || !program.since || !program.till) return null;
      return { id: programId, channelUuid: channelIdentifier, since: program.since, till: program.till, resizeType: 'left' };
    },
    canDrag: () => {
      const channelIdentifier = program.channelUuid || program.channelId || program.assetNumber || program.channel || program.machineId;
      const programId = program.originalId || program.id;
      return !!(programId && channelIdentifier && program.since && program.till);
    },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  }));

  const [{ isDragging: isResizingRight }, dragRight] = useDrag(() => ({
    type: DRAG_TYPES.RESIZE_RIGHT,
    item: () => {
      const channelIdentifier = program.channelUuid || program.channelId || program.assetNumber || program.channel || program.machineId;
      const programId = program.originalId || program.id;
      if (!programId || !channelIdentifier || !program.since || !program.till) return null;
      return { id: programId, channelUuid: channelIdentifier, since: program.since, till: program.till, resizeType: 'right' };
    },
    canDrag: () => {
      const channelIdentifier = program.channelUuid || program.channelId || program.assetNumber || program.channel || program.machineId;
      const programId = program.originalId || program.id;
      return !!(programId && channelIdentifier && program.since && program.till);
    },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  }));

  if (!program.since || !program.till) return null;

  const isSegment = program.isSegment || false;
  const segmentYear = program.segmentYear;
  const isFirstSegment = program.isFirstSegment || false;
  const isLastSegment = program.isLastSegment || false;

  let leftDays, durationDays;
  if (headerData && headerData[0]?.viewMode === 'year-multi') {
    // Multi-year view: use column index lookup (similar to year view with months)
    // Get the first year from headerData for fallback calculations
    const firstHeaderYear = headerData[0]?.year !== undefined
      ? headerData[0].year
      : (headerData[0]?.iso ? new Date(headerData[0].iso).getFullYear() : new Date().getFullYear());
    
    if (isSegment && segmentYear !== undefined) {
      // Segment: find the column index that matches this segment's year
      const targetYear = Number(segmentYear); // Ensure numeric comparison
      
      // Find matching column in headerData
      let columnIndex = -1;
      for (let i = 0; i < headerData.length; i++) {
        const headerYear = headerData[i].year !== undefined 
          ? Number(headerData[i].year) 
          : new Date(headerData[i].iso || headerData[i].date).getFullYear();
        // Strict numeric comparison to avoid type mismatches
        if (headerYear === targetYear) {
          columnIndex = i;
          break;
        }
      }
      
      // Fallback: calculate from first header year if column not found
      if (columnIndex === -1) {
        columnIndex = targetYear - firstHeaderYear;
      }
      
      // Debug logging for multi-year positioning
      if ((program.id === '1444' || program.id === 1444) && targetYear >= 2024 && targetYear <= 2026) {
        console.log('[Multi-Year Debug] Program segment positioning:', {
          programId: program.id,
          originalId: program.originalId,
          segmentYear: targetYear,
          targetYear: targetYear,
          firstHeaderYear: firstHeaderYear,
          columnIndex: columnIndex,
          headerDataYears: headerData.map((h, idx) => ({
            index: idx,
            year: h.year,
            iso: h.iso,
            extractedYear: h.year !== undefined ? Number(h.year) : new Date(h.iso || h.date).getFullYear()
          }))
        });
      }
      
      leftDays = columnIndex >= 0 ? columnIndex : 0;
      durationDays = 1; // Each segment is exactly one year column
    } else {
      // Non-segmented program: find start and end year columns
      const programStart = toDateOnly(program.since);
      const programEnd = toDateOnly(program.till);
      const startYear = programStart?.getFullYear() ?? 0;
      const endYear = programEnd?.getFullYear() ?? 0;
      
      // Find column indices in headerData
      let startColumnIndex = -1;
      let endColumnIndex = -1;
      
      for (let i = 0; i < headerData.length; i++) {
        const headerYear = headerData[i].year !== undefined 
          ? Number(headerData[i].year) 
          : new Date(headerData[i].iso || headerData[i].date).getFullYear();
        
        // Strict numeric comparison
        if (Number(headerYear) === Number(startYear) && startColumnIndex === -1) {
          startColumnIndex = i;
        }
        if (Number(headerYear) === Number(endYear)) {
          endColumnIndex = i;
        }
      }
      
      // Fallback: calculate from first header year if columns not found
      if (startColumnIndex === -1 || endColumnIndex === -1) {
        startColumnIndex = startYear - firstHeaderYear;
        endColumnIndex = endYear - firstHeaderYear;
      }
      
      leftDays = Math.max(0, startColumnIndex >= 0 ? startColumnIndex : 0);
      // Duration = number of year columns spanned (inclusive)
      durationDays = Math.max(1, (endColumnIndex >= 0 ? endColumnIndex : startColumnIndex) - leftDays + 1);
    }
  } else if (headerData && headerData[0]?.viewMode === 'month') {
    // Year view with month columns: use column index directly (like multi-year view)
    // Similar to multi-year: leftDays = column index, durationDays = number of columns
    // Note: We check headerData viewMode, not the viewMode prop, since ChannelRow passes headerData viewMode
    
    if (isSegment && program.segmentMonth !== undefined && program.segmentYear !== undefined) {
      // Segment: find the column index that matches this segment's month
      const segmentMonthIndex = program.segmentMonth;
      
      // Find matching column in headerData
      let columnIndex = -1;
      for (let i = 0; i < headerData.length; i++) {
        const headerMonthIdx = headerData[i].monthIndex;
        if (headerMonthIdx === segmentMonthIndex) {
          columnIndex = i;
          break;
        }
      }
      
      // Fallback: if monthIndex not found, calculate from month
      if (columnIndex === -1 && headerData.length === 12) {
        columnIndex = segmentMonthIndex; // For full year, monthIndex matches column index
      }
      
      leftDays = columnIndex >= 0 ? columnIndex : 0;
      durationDays = 1; // Each segment is exactly one month column
    } else {
      // Non-segmented program: find start and end month columns
      const programStart = toDateOnly(program.since);
      const programEnd = toDateOnly(program.till);
      const startMonthIdx = programStart?.getMonth() ?? 0;
      const endMonthIdx = programEnd?.getMonth() ?? 0;
      
      // Find column indices in headerData
      let startColumnIndex = -1;
      let endColumnIndex = -1;
      
      for (let i = 0; i < headerData.length; i++) {
        const headerMonthIdx = headerData[i].monthIndex;
        if (headerMonthIdx === startMonthIdx && startColumnIndex === -1) {
          startColumnIndex = i;
        }
        if (headerMonthIdx === endMonthIdx) {
          endColumnIndex = i;
        }
      }
      
      // Fallback: if headerData has 12 months (0-11), monthIndex = column index
      if ((startColumnIndex === -1 || endColumnIndex === -1) && headerData.length === 12) {
        startColumnIndex = startMonthIdx;
        endColumnIndex = endMonthIdx;
      }
      
      leftDays = Math.max(0, startColumnIndex >= 0 ? startColumnIndex : 0);
      // Duration = number of month columns spanned (inclusive)
      durationDays = Math.max(1, (endColumnIndex >= 0 ? endColumnIndex : startColumnIndex) - leftDays + 1);
    }
  } else {
    leftDays = program.since && startIso ? differenceInCalendarDays(toDateOnly(program.since), toDateOnly(startIso)) : 0;
    const baseDurationDays = program.since && program.till ? Math.max(1, differenceInCalendarDays(toDateOnly(program.till), toDateOnly(program.since)) + 1) : 1;
    durationDays = baseDurationDays;
  }

  const bg = getProgramColor(program);
  const segments = getSegmentedColors(program, programCompletion);

  const getText = () => {
    const wt = program.workTicketNo || program.workTicketNumber || program.workTicket || program.workorderid || program.workOrderId || program.processIdExt || program.wtname || program.title || '';
    const part = program.partNumber || program.partNo || program.part_code || program.partCode || program.standarD_ID || program.itemNumber || program.itemCode || program.description || '';
    return wt && part ? `${wt} - ${part}` : wt || part || '';
  };

  let dateLabel;
  const displaySince = program.originalSince || program.since;
  const displayTill = program.originalTill || program.till;
  if (displaySince && displayTill) {
    const startDate = toDateOnly(displaySince);
    const endDate = toDateOnly(displayTill);
    const startYear = startDate.getFullYear();
    const endYear = endDate.getFullYear();
    dateLabel = startYear === endYear ? `${format(startDate, 'dd.MM')} - ${format(endDate, 'dd.MM')}` : `${format(startDate, 'dd.MM.yyyy')} - ${format(endDate, 'dd.MM.yyyy')}`;
  } else {
    dateLabel = 'No dates';
  }

  const segmentWidth = isSegment ? cellWidth : durationDays * cellWidth - 8;
  const baseHeight = 46;
  const barHeight = baseHeight;
  const horizontalSpacing = 2;
  // For month grid: check headerData viewMode (year view with month columns)
  // Note: We check headerData viewMode directly since ChannelRow passes headerData viewMode as the viewMode prop
  const isMonthGrid = headerData && headerData[0]?.viewMode === 'month';
  const isYearMulti = headerData && headerData[0]?.viewMode === 'year-multi';
  
  // Enable horizontal stacking for month grid and year-multi views (similar to multi-year view)
  const enableHorizontalStacking = isMonthGrid || isYearMulti;
  const reducedWidth = overlapCount > 1 && enableHorizontalStacking 
    ? segmentWidth / overlapCount - horizontalSpacing 
    : segmentWidth;
  const horizontalOffset = overlapCount > 1 && enableHorizontalStacking 
    ? horizontalIdx * (reducedWidth + horizontalSpacing) 
    : 0;

  let segmentLeft;
  // For multi-year and month views: always use the program's own year/month for base positioning
  // overlapGroupStart is only used for horizontal stacking offset within the same column
  // For day/hour views: use overlapGroupStart for base positioning
  // Note: isYearMulti and isMonthGrid are already declared above for horizontal stacking logic
  
  if (overlapCount > 1 && program.overlapGroupStart && !isYearMulti && !isMonthGrid) {
    // Only use overlapGroupStart for day/hour views (not for multi-year or month views)
    const overlapStart = toDateOnly(program.overlapGroupStart);
    const timelineStart2 = toDateOnly(startIso);
    const overlapLeftDays = differenceInCalendarDays(overlapStart, timelineStart2);
    segmentLeft = overlapLeftDays * cellWidth;
  } else {
    // For multi-year and month views: always use the program's own column position (leftDays)
    // The horizontal offset is already handled separately for stacking within the column
    segmentLeft = leftDays * cellWidth;
  }

  return (
    <div
      id={`program-${program.id}`}
      className={isSearchMatch ? 'planner-bar planner-bar--search-match' : 'planner-bar'}
      style={{ position: 'absolute', left: segmentLeft + horizontalOffset, width: Math.max(reducedWidth, 40), height: barHeight, zIndex: (isSegment ? (program.segmentIndex || 0) + 1 : 1) + horizontalIdx }}
    >
      <div
        ref={drag}
        onDoubleClick={onDoubleClick}
        onMouseEnter={() => setIsHoveringBar(true)}
        onMouseLeave={() => setIsHoveringBar(false)}
        style={{ width: '100%', height: '100%', border: '2px solid rgba(0,0,0,0.2)', borderTopLeftRadius: (!isSegment) ? 8 : (isSegment && program.isFirstSegment ? 8 : 0), borderBottomLeftRadius: (!isSegment) ? 8 : (isSegment && program.isFirstSegment ? 8 : 0), borderTopRightRadius: (!isSegment) ? 8 : (isSegment && program.isLastSegment ? 8 : 0), borderBottomRightRadius: (!isSegment) ? 8 : (isSegment && program.isLastSegment ? 8 : 0), boxShadow: '0 2px 8px rgba(0,0,0,0.07)', cursor: 'move', overflow: 'hidden', position: 'relative', display: 'flex', alignItems: 'stretch' }}
        title={program.since && program.till ? `${getText()} | ${format(parseISO(program.since), 'dd MMM HH:mm')} - ${format(parseISO(program.till), 'dd MMM HH:mm')}` : getText()}
      >
        {/* Resize handles */}
        {/* Left handle with indicator */}
        <div
          ref={dragLeft}
          onMouseEnter={() => setIsHoveringResizeHandle(true)}
          onMouseLeave={() => setIsHoveringResizeHandle(false)}
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 12,
            cursor: 'ew-resize',
            zIndex: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
          }}
        >
          <div
            style={{
              width: 2,
              height: '60%',
              backgroundColor: isHoveringResizeHandle ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.25)',
              borderRadius: 2,
              transition: 'background-color 0.15s ease',
              marginLeft: 4,
            }}
          />
        </div>
        {/* Right handle with indicator */}
        <div
          ref={dragRight}
          onMouseEnter={() => setIsHoveringResizeHandle(true)}
          onMouseLeave={() => setIsHoveringResizeHandle(false)}
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: 12,
            cursor: 'ew-resize',
            zIndex: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <div
            style={{
              width: 2,
              height: '60%',
              backgroundColor: isHoveringResizeHandle ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.25)',
              borderRadius: 2,
              transition: 'background-color 0.15s ease',
              marginRight: 4,
            }}
          />
        </div>
        {/* Always render with white background and green fill based on completion percentage */}
        <div style={{ 
          width: '100%', 
          height: '100%', 
          backgroundColor: segments?.pendingColor || '#FFFFFF', 
          position: 'relative',
          overflow: 'hidden'
        }}>
          {segments && segments.usePercentage && segments.percentage !== undefined && segments.percentage > 0 && (
            <div style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: `${segments.percentage}%`,
              minWidth: segments.percentage > 0 ? '3px' : '0', // Ensure minimum 3px for visibility
              backgroundColor: segments?.completedColor || '#4CAF50',
              transition: 'width 0.3s ease',
              zIndex: 0 // Ensure green is behind text
            }} />
          )}
        </div>
        {(!isSegment || isFirstSegment) && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: totalSegmentWidth ? undefined : 0, bottom: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', paddingLeft: (!isSegment || isFirstSegment) ? (barHeight < 40 ? '12px' : '16px') : (barHeight < 40 ? '8px' : '10px'), paddingRight: (!isSegment || isLastSegment) ? (barHeight < 40 ? '12px' : '16px') : (barHeight < 40 ? '8px' : '10px'), paddingTop: barHeight < 40 ? '3px' : '6px', paddingBottom: barHeight < 40 ? '3px' : '6px', pointerEvents: 'none', zIndex: 1 }}>
            <div style={{ fontWeight: 600, color: '#333333', fontSize: barHeight < 40 ? 11 : 13, lineHeight: 1.1, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{getText()}</div>
            {barHeight >= 35 && <div style={{ color: '#666666', fontSize: barHeight < 40 ? 10 : 12 }}>{dateLabel}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

export default ProgramBar;


